import { useId, useRef, type ComponentProps, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type FloatingLabelInputStatus = 'default' | 'error' | 'success';

export interface FloatingLabelInputProps extends Omit<ComponentProps<'input'>, 'placeholder' | 'prefix'> {
    /** Shown as the placeholder while the box is empty and unfocused; floats onto the top border on focus / once it has a value. */
    label: string;
    /** Content fixed inside the box before the text (e.g. "+91"). Never floats. */
    prefix?: ReactNode;
    /** Content fixed inside the box after the text (e.g. a Verify button, "Verified", an icon). Never floats. */
    suffix?: ReactNode;
    status?: FloatingLabelInputStatus;
    /** Classes for the bordered box (height, background, border colour overrides). */
    containerClassName?: string;
    /** Classes for the floated label (e.g. its background when the field sits on a non-white surface). */
    labelClassName?: string;
}

/** Shared with FloatingLabelSelect so both field types look identical. */
export const STATUS_BORDER: Record<FloatingLabelInputStatus, string> = {
    default: 'border-[color:var(--input-selected-active,#262626)]',
    error: 'border-destructive',
    success: 'border-[color:var(--input-success,#418334)]',
};

export const STATUS_LABEL: Record<FloatingLabelInputStatus, string> = {
    default: 'text-[#555555]',
    error: 'text-destructive',
    success: 'text-[color:var(--input-success,#418334)]',
};

/**
 * Text box whose placeholder becomes a label on the top border as soon as the box is focused or has a value
 * (typed, autofilled or pre-filled), and drops back to a placeholder when it is empty and loses focus.
 *
 * Pure CSS: the resting text is the input's own placeholder (so it always sits after any prefix), and the
 * floated <label> is revealed via :focus-within / :placeholder-shown — no JS state, so SSR and autofill
 * render correctly. Prefix / suffix content stays inside the box at all times.
 *
 * Deliberately not `data-slot="input"`, so page-level overrides aimed at the plain Input don't restyle it.
 *
 * Sizing lives on the <input> itself (padding around the placeholder / text, inspectable in DevTools), driven by
 * CSS variables a page can override per context (see base.css):
 *   --fl-h   box height                     (default 62px)
 *   --fl-py  input padding top / bottom     (default 20px)
 *   --fl-px  input padding left / right     (default 8.5px) — also the outer inset of any prefix / suffix
 */
export function FloatingLabelInput({
    label,
    prefix,
    suffix,
    status = 'default',
    id,
    className,
    containerClassName,
    labelClassName,
    ref,
    ...props
}: FloatingLabelInputProps) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRefs = (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
    };

    return (
        <div
            data-slot="floating-label-input"
            // Clicking the prefix / padding focuses the input; the suffix keeps its own clicks (buttons, links).
            onMouseDown={(e) => {
                const target = e.target as HTMLElement;
                if (target === inputRef.current || target.closest('button, a, input, select, textarea')) return;
                e.preventDefault();
                inputRef.current?.focus();
            }}
            className={cn(
                'group relative flex h-[var(--fl-h,62px)] w-full cursor-text items-center gap-2 rounded border border-solid bg-white transition-colors',
                STATUS_BORDER[status],
                // Form-library errors (aria-invalid) turn the border red even without an explicit status.
                status === 'default' && 'has-[input[aria-invalid=true]]:border-destructive',
                containerClassName
            )}>
            <label
                htmlFor={inputId}
                className={cn(
                    'font-lato pointer-events-none absolute left-3 z-10 bg-white px-1 text-xs leading-4 font-normal',
                    'top-[calc(50%-8px)] -translate-y-1/2 opacity-0',
                    'transition-[top,translate,opacity] duration-300 ease-out',
                    'group-focus-within:top-[-8px] group-focus-within:translate-y-0 group-focus-within:opacity-100',
                    'group-has-[input:not(:placeholder-shown)]:top-[-8px] group-has-[input:not(:placeholder-shown)]:translate-y-0 group-has-[input:not(:placeholder-shown)]:opacity-100',
                    STATUS_LABEL[status],
                    status === 'default' && 'group-has-[input[aria-invalid=true]]:text-destructive',
                    labelClassName
                )}>
                {label}
            </label>

            {prefix != null && (
                <span className="font-lato shrink-0 pl-[var(--fl-px,8.5px)] text-base leading-[22px] font-normal text-[#555555]">
                    {prefix}
                </span>
            )}

            <input
                ref={setRefs}
                id={inputId}
                placeholder={label}
                className={cn(
                    'font-lato h-full min-w-0 flex-1 bg-transparent py-[var(--fl-py,20px)] text-base leading-[22px] font-normal tracking-normal text-[#17171B] outline-none',
                    // Left / right padding sits on the input unless a prefix / suffix takes that edge.
                    prefix != null ? 'pl-0' : 'pl-[var(--fl-px,8.5px)]',
                    suffix != null ? 'pr-0' : 'pr-[var(--fl-px,8.5px)]',
                    'placeholder:text-[#7B7B7B] focus:placeholder:text-transparent',
                    'disabled:cursor-not-allowed',
                    className
                )}
                {...props}
            />

            {suffix != null && <div className="flex shrink-0 items-center pr-[var(--fl-px,8.5px)]">{suffix}</div>}
        </div>
    );
}
